import React, { useState, useEffect } from "react";
import { TrendingUp, TrendingDown, Wallet, ArrowUpRight } from "lucide-react";

function AnimatedNumber({ value, prefix = "" }) {
  const [displayed, setDisplayed] = useState(0);
  useEffect(() => {
    let start = 0;
    const end = Math.round(value);
    const duration = 1200;
    const step = end / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setDisplayed(end); clearInterval(timer); }
      else setDisplayed(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [value]);
  return <span>{prefix}{displayed.toLocaleString()}</span>;
}

export default function SummaryCards({ transactions }) {
  const income = transactions.filter((t) => t.type === "income").reduce((s, t) => s + t.amount, 0);
  const expenses = transactions.filter((t) => t.type === "expense").reduce((s, t) => s + t.amount, 0);
  const balance = income - expenses;
  const savingsRate = income > 0 ? ((balance / income) * 100).toFixed(1) : 0;

  const cards = [
    {
      label: "Total Balance",
      value: balance,
      prefix: "$",
      icon: Wallet,
      color: "cyan",
      borderColor: "border-accent-cyan/20",
      bgColor: "bg-accent-cyan/5",
      iconColor: "text-accent-cyan",
      glowColor: "shadow-accent-cyan/10",
      trend: `${savingsRate}% savings rate`,
      trendUp: balance > 0,
    },
    {
      label: "Total Income",
      value: income,
      prefix: "$",
      icon: TrendingUp,
      color: "green",
      borderColor: "border-accent-green/20",
      bgColor: "bg-accent-green/5",
      iconColor: "text-accent-green",
      glowColor: "shadow-accent-green/10",
      trend: `${transactions.filter((t) => t.type === "income").length} transactions`,
      trendUp: true,
    },
    {
      label: "Total Expenses",
      value: expenses,
      prefix: "$",
      icon: TrendingDown,
      color: "red",
      borderColor: "border-accent-red/20",
      bgColor: "bg-accent-red/5",
      iconColor: "text-accent-red",
      glowColor: "shadow-accent-red/10",
      trend: `${transactions.filter((t) => t.type === "expense").length} transactions`,
      trendUp: false,
    },
    {
      label: "Net Savings",
      value: Math.max(0, balance),
      prefix: "$",
      icon: ArrowUpRight,
      color: "purple",
      borderColor: "border-accent-purple/20",
      bgColor: "bg-accent-purple/5",
      iconColor: "text-accent-purple",
      glowColor: "shadow-accent-purple/10",
      trend: `${savingsRate}% of income`,
      trendUp: balance > 0,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      {cards.map(({ label, value, prefix, icon: Icon, borderColor, bgColor, iconColor, glowColor, trend, trendUp }, i) => (
        <div
          key={label}
          style={{ animationDelay: `${i * 100}ms` }}
          className={`
            animate-slide-up relative overflow-hidden rounded-2xl border ${borderColor}
            bg-bg-card p-5 shadow-xl ${glowColor}
            hover:shadow-2xl transition-all duration-500 group cursor-default
            hover:-translate-y-1
          `}
        >

          <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full ${bgColor} blur-2xl opacity-60 group-hover:opacity-100 transition-opacity duration-500`} />

          <div className="relative">
            <div className="flex items-start justify-between mb-4">
              <p className="text-xs font-body text-white/40 uppercase tracking-widest">{label}</p>
              <div className={`w-8 h-8 rounded-xl ${bgColor} border ${borderColor} flex items-center justify-center`}>
                <Icon size={15} className={iconColor} />
              </div>
            </div>
            <p className="font-display font-bold text-2xl text-white mb-2">
              <AnimatedNumber value={value} prefix={prefix} />
            </p>
            <p className="text-xs font-body text-white/30">{trend}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
